"use client";

import React from "react";
import { useController, Control, FieldPath, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { cn } from "../../../lib/src/utils";
import { X } from "lucide-react";

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TName;
  control: Control<TFieldValues>;
  label: string;
  description?: string;
  helperText?: string;
  errorText?: string;
  options: MultiSelectOption[];
  className?: string;
  required?: boolean;
  disabled?: boolean;
  maxSelections?: number;
}

export function MultiSelectInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  label,
  description,
  helperText,
  errorText,
  options,
  className,
  required = false,
  disabled = false,
  maxSelections,
}: MultiSelectInputProps<TFieldValues, TName>) {
  const {
    field: { onChange, value = [] },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const hasError = error || errorText;

  const handleToggle = (optionValue: string) => {
    if (disabled) return;
    
    const currentValues = (Array.isArray(value) ? value : []) as any[];
    const isSelected = currentValues.includes(optionValue);
    
    if (isSelected) {
      onChange(currentValues.filter((val: any) => val !== optionValue));
    } else {
      if (maxSelections && currentValues.length >= maxSelections) {
        return;
      }
      onChange([...currentValues, optionValue]);
    }
  };

  const handleRemove = (optionValue: string) => {
    if (disabled) return;
    
    const currentValues = (Array.isArray(value) ? value : []) as any[];
    onChange(currentValues.filter((val) => val !== optionValue));
  };

  const selectedValues = (Array.isArray(value) ? value : []) as any[];
  const selectedOptions = options.filter((option) => selectedValues.includes(option.value));

  return (
    <FormItem className={cn("space-y-2", className)}>
      <FormLabel className={cn(required && "after:content-['*'] after:ml-0.5 after:text-red-500")}>
        {label}
        {maxSelections && (
          <span className="text-sm text-muted-foreground ml-2">
            ({selectedValues.length}/{maxSelections})
          </span>
        )}
      </FormLabel>
      
      {/* Selected items */}
      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedOptions.map((option) => (
            <Badge key={option.value} variant="secondary" className="pr-1">
              {option.label}
              <button
                type="button"
                onClick={() => handleRemove(option.value)}
                disabled={disabled}
                className="ml-1 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <FormControl>
        <div className={cn(
          "space-y-2 p-3 border rounded-md",
          hasError && "border-destructive",
          disabled && "opacity-50 cursor-not-allowed"
        )}>
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            const isDisabled = disabled || Boolean(maxSelections && !isSelected && selectedValues.length >= maxSelections);
            
            return (
              <div key={option.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`${name}-${option.value}`}
                  checked={isSelected}
                  onCheckedChange={() => handleToggle(option.value)}
                  disabled={isDisabled}
                />
                <label
                  htmlFor={`${name}-${option.value}`}
                  className={cn(
                    "text-sm font-medium leading-none cursor-pointer",
                    isDisabled ? "text-muted-foreground cursor-not-allowed" : ""
                  )}
                >
                  {option.label}
                </label>
              </div>
            );
          })}
        </div>
      </FormControl>
      
      {description && <FormDescription className="text-sm text-muted-foreground">{description}</FormDescription>}
      {helperText && !hasError && <FormDescription className="text-sm text-muted-foreground">{helperText}</FormDescription>}
      {hasError && <FormMessage>{errorText || error?.message}</FormMessage>}
    </FormItem>
  );
}
